import styled from 'styled-components';
import { setIconFillColor } from '../../styles/mixins';
import { IStyledChipProps, TChipSize } from './HBChip.types';
import { CHIP_STRUCTURE } from './HBChip.structure';
import { getChipColors } from './HBChip.colors';
import spacing from '../../styles/spacing';
import HBClickable from '../HBClickable/HBClickable';
import { vars } from '../../theme';

export const LabelWrapper = styled.span<{
    $customStyles?: React.CSSProperties;
    $hasMaxWidth?: boolean;
}>`
    ${({ $hasMaxWidth }) => $hasMaxWidth && 'min-width: 0;'}
    ${({ $customStyles }) => $customStyles && { ...$customStyles }}
`;

export const TitleWrapper = styled(LabelWrapper)`
    margin-right: ${spacing[4]};
`;

export const StyledChip = styled(HBClickable)<IStyledChipProps>`
    user-select: none;
    display: flex;
    align-items: center;
    width: fit-content;
    max-width: ${({ $maxWidth }) => $maxWidth || 'none'};
    white-space: nowrap;
    font-family: ${vars.fonts.body};

    ${({ $size }) => {
        const definitions = CHIP_STRUCTURE[$size];
        return `
        height: ${definitions.height};
        padding: 0 ${definitions.padding};
        border-radius: ${definitions.height};

        svg {
            width: ${definitions.iconLineHeight};
            height: ${definitions.iconLineHeight};
        }
        `;
    }}

    ${({ $theme, $selected, isDisabled }) => {
        const styles = getChipColors($theme, $selected, isDisabled);
        // Note: We're using 1.5px border width for selected chips to improve accessibility.
        // 1px would not be different enough in high contrast mode, 2px would be too prominent for a component of this size.
        // Rendering was checked on non-retina screens and was ok.
        const borderWidth = $selected ? '1.5px' : '1px';

        return `
      background-color: ${styles.bg};
      color: ${styles.text};
      box-shadow: inset 0 0 0 ${borderWidth} ${styles.border};

      ${
          !isDisabled &&
          `
        &:focus-visible,
        &:hover {
          background-color: ${styles.hoverBg};
          box-shadow: inset 0 0 0 ${borderWidth} ${styles.border};
        }

        &:active {
            background-color: ${styles.pressedBg};
        }
        `
      }
    `;
    }}

    ${({ $customStyles }) => $customStyles && { ...$customStyles }}

    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${vars.colors.stroke.keyboardFocus};
        outline-offset: 1px;
    }
`;

export const ContentWrapper = styled.div<{
    $placement: 'prefix' | 'suffix';
    $size: TChipSize;
    $iconColor: string;
}>`
    display: flex;
    align-items: center;
    margin-right: ${({ $placement, $size }) =>
        $placement === 'prefix' ? CHIP_STRUCTURE[$size].iconGap : '0px'};
    margin-left: ${({ $placement, $size }) =>
        $placement === 'suffix' ? CHIP_STRUCTURE[$size].iconGap : '0px'};
    ${({ $iconColor }) => setIconFillColor($iconColor)}

    i {
        display: flex;
        align-items: center;
        line-height: ${({ $size }) => CHIP_STRUCTURE[$size].iconLineHeight};
    }
`;
